import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  googleAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { Alert, Box, Flex, Link, Skeleton, Text } from '@backstage/ui';
import { applyImplies, humanise } from './schemaForm';
import type { JsonValue } from './schemaForm';
import { PipelineForm } from './PipelineForm';
import { fetchPipelineManifest } from './pipelineManifest';
import type { PipelineDescriptor } from './pipelineManifest';

const CONTROL_ID_TOKEN_SECRET = 'CONTROL_ID_TOKEN';

/**
 * The fields the TEMPLATE supplies, which must never appear in this form.
 *
 * Identity comes from the earlier steps, and the target comes from policy via
 * PlacementPicker. Rendering them here would give someone a second, editable,
 * silently-losing copy of a value they already set — and `project` in
 * particular is the one field the whole placement design exists to keep out of
 * a text box.
 */
const TEMPLATE_OWNED = new Set([
  'name',
  'repo',
  'project',
  'env',
  'cloud',
  'template',
  'scaffold',
  'team',
]);

/**
 * Everything a service can ask for, rendered from Control's own schema.
 *
 * # Where the form comes from
 *
 * `GET /api/control/pipelines/manifest`. Control generates the JSON Schema from
 * the Zod object its route validates with, so this form cannot offer a field the
 * endpoint would reject, and cannot keep offering one that was renamed.
 *
 * That matters because the alternative was tried. This form used to be six
 * fields written out in `template.yaml` — six of the roughly twenty-five
 * `terraform/gcp/SERVICES.md` documents — and three of the six had drifted from
 * what Control accepted. Every drift surfaced as a 400 in front of a person, and
 * two of them after a repository had already been created.
 *
 * So there is no list of options in this repository. There is a fetch.
 *
 * # Why it is grouped, and why most of it is hidden
 *
 * Thirty-three inputs is a wall, and the audience includes people who have
 * never heard of Cloud Build. The manifest's `ui.groups` orders the handful
 * most services change; everything else — and anything a new spec field adds
 * that nobody has grouped yet — falls into "Advanced", collapsed. A field
 * nobody placed looks unowned rather than disappearing, which is the failure
 * mode worth having.
 */
export const ServiceOptionsField = (
  props: FieldExtensionComponentProps<Record<string, JsonValue>>,
) => {
  const {
    onChange,
    schema: fieldSchema,
    formData,
    formContext,
    uiSchema,
  } = props;
  // `required`/`rawErrors` are not read: this field is a container of other
  // fields, and RJSF validates the object it emits rather than this wrapper.
  // The old MUI version rendered ScaffolderField's error furniture around it,
  // which drew an empty error slot on every render.
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const googleAuth = useApi(googleAuthApiRef);
  const { setSecrets } = useTemplateSecrets();

  const pipelineId = String(
    uiSchema?.['ui:options']?.pipeline ?? 'new-service',
  );

  const [pipeline, setPipeline] = useState<PipelineDescriptor>();
  const [error, setError] = useState<string>();
  const [values, setValues] = useState<Record<string, JsonValue>>(
    formData ?? {},
  );

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // `set` is created once, so it cannot close over `pipeline` — and it needs
  // the field metadata to know whether an answer implies anything.
  const pipelineRef = useRef<PipelineDescriptor | undefined>(undefined);
  pipelineRef.current = pipeline;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // The caller's identity, for the steps that submit. Collected here too
      // so this field works in a template with no PlacementPicker — and in its
      // OWN try, because it is not what the manifest read needs. Sharing a try
      // with the fetch is what turned an empty `getIdToken()` into "Could not
      // read what Control accepts", which is a different and much more alarming
      // sentence than the truth.
      try {
        const idToken = await googleAuth.getIdToken();
        if (idToken) setSecrets({ [CONTROL_ID_TOKEN_SECRET]: idToken });
      } catch {
        // PlacementPicker owns saying so, with a button. Silence here rather
        // than two components reporting the same missing token.
      }

      try {
        // No caller token: the manifest is a capability document, read with
        // the portal's own identity server-side.
        const pipelines = await fetchPipelineManifest(discovery, fetchApi);
        const found = pipelines.find(p => p.id === pipelineId);
        if (!found) {
          throw new Error(
            `Control publishes no pipeline "${pipelineId}". It knows: ${
              pipelines.map(p => p.id).join(', ') || '(none)'
            }`,
          );
        }
        if (!cancelled) setPipeline(found);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per mount. The manifest changes when Control is redeployed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback((key: string, value: JsonValue | undefined) => {
    setValues(previous => {
      let next = { ...previous };
      // Omit rather than send undefined: Control's Zod applies its own defaults,
      // and an explicit undefined for `attachDatabase` is not the same as
      // letting terraform's default stand.
      if (value === undefined || value === '') delete next[key];
      else next[key] = value;
      // Answering one field can fill in others: ticking sign-in adds the
      // `auth-secret` the app reads at boot. The dependency is Control's to
      // state and this is only the walk — see applyImplies. After the
      // assignment above, so the triggering answer is part of what it sees.
      next = applyImplies(pipelineRef.current?.ui.fields?.[key], value, next);
      onChangeRef.current(next);
      return next;
    });
  }, []);

  /**
   * What `ui.pattern` may interpolate.
   *
   * The service name comes from the step before this one, so a bucket can be
   * previewed as `answerrocket-demo-myapp-uploads` while somebody is still
   * typing `uploads`. Both fall back to their own placeholder rather than to
   * an empty string — `-myapp-uploads` would read as a real name with a typo.
   */
  const patternContext = useMemo(() => {
    const outer = (
      formContext as { formData?: { name?: string; placement?: unknown } }
    )?.formData;
    const placement = outer?.placement as { project?: string } | undefined;
    return {
      name: outer?.name || '{app}',
      project: placement?.project || '{project}',
    };
  }, [formContext]);

  return (
    <Box>
      <Text
        as="p"
        variant="body-small"
        color="secondary"
        style={{ marginBottom: 16 }}
      >
        {fieldSchema.description ??
          'What this service needs. Everything here is optional — the defaults are the paved road.'}
      </Text>

      {error && (
        <Alert
          status="warning"
          title="Could not read what Control accepts"
          description={`${error}. Without it this form would be guessing, so it is showing nothing rather than something wrong.`}
        />
      )}

      {!pipeline && !error && (
        <Flex direction="column" gap="3">
          <Skeleton style={{ height: 56 }} />
          <Skeleton style={{ height: 56 }} />
          <Skeleton style={{ height: 56 }} />
        </Flex>
      )}

      {pipeline && (
        <>
          {pipeline.docs && (
            <Text
              as="p"
              variant="body-small"
              color="secondary"
              style={{ marginBottom: 20 }}
            >
              Every option here is documented in{' '}
              <Link href={pipeline.docs}>SERVICES.md</Link>, which is the source
              of truth for what a service can ask for.
            </Text>
          )}

          <PipelineForm
            flat
            pipeline={pipeline}
            values={values}
            onSet={set}
            exclude={TEMPLATE_OWNED}
            context={patternContext}
          />
        </>
      )}
    </Box>
  );
};

export { humanise };
